import { useMutation, useQuery } from "@apollo/client";
import { Menu, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { Cog8ToothIcon, ArrowRightStartOnRectangleIcon, ArrowsRightLeftIcon, UserCircleIcon as UCIco } from "@heroicons/react/24/outline";
import { UserIcon } from "@heroicons/react/24/solid";
// import UserIcon from "components/UserIcon/UserIcon";
import { useSession } from "contexts";
import { GetMroEmployeeDocument, GetOrganizationsDocument, GetUserInfoDocument, SwitchOrganizationDocument } from "graphql/generated";
import { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import isMobileApp from "utils/isMobileApp";
import { OrgTypes } from "utils/orgTypes";
import Button from "components/Button/Button";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function UserMenuMobile({ className, sidebarOpen, altLoginUrl } 
    : { className?: string; sidebarOpen?: boolean; altLoginUrl?: string}) {
    const { organizationId, setOrganizationId, logout, user, organizationType, setCraftId, setLoginSession, setOrganizationType } = useSession();
    const { data: { organizations } = { organizations: [] } } = useQuery(GetOrganizationsDocument, { variables: { where: { userOrganizationProfiles: { 
        some: { user: { is:{ id: { equals: user?.id } } } } 
    } } } });
    const [switchOrganization] = useMutation(SwitchOrganizationDocument);
    const { data: { mroEmployeeProfile } = {} } = useQuery(GetMroEmployeeDocument, {
        variables: { mroEmployeeId: user?.mroEmployeeProfileId },
        skip: !user?.mroEmployeeProfileId,
    }); 
    const { data: { me } = {}, client } = useQuery(GetUserInfoDocument);
    const navigate = useNavigate();
    const userNavigation = [
        { name: 'Settings', href: `${ organizationType === OrgTypes.PART145 ? '/mro' : ''}/account/settings`, icon: Cog8ToothIcon },
    ];
    const firstName =  organizationType === OrgTypes.PART145 ? mroEmployeeProfile?.firstName : (me?.firstName ?? "\xa0");
    const lastName =  organizationType === OrgTypes.PART145 ? mroEmployeeProfile?.lastName : (me?.lastName ?? "\xa0");
    const orgName = organizationType === OrgTypes.PART145 ? mroEmployeeProfile?.mroOrganization?.name : organizations.find((o) => o.id === organizationId)?.name;

    return (
      <Menu as="div" className={'md:group flex justify-center w-full h-full' + (sidebarOpen ? '' : '')}>
        <Menu.Button className={'w-full h-full justify-center flex items-center' + (sidebarOpen ? ' p-2' : '')}>
          {/* <div className="flex items-center w-full justify-end md:justify-center py-1">
            <UserIcon
              iconSize="xs"
              iconText={firstName + ' ' + lastName}
              iconId={firstName + ' ' + lastName}
              iconColor={mroEmployeeProfile?.profileColor}
              className={'flex ml-0 z-[100] ' + className}
            />
          </div> */}
          <UserIcon className="h-6 w-6 mb-4"/>
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="transform opacity-0"
          enterTo="transform opacity-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100"
          leaveTo="transform opacity-0">
          <Menu.Items
            className={`fixed origin-center w-screen bottom-16 pb-2 left-0 focus:outline-none text-base flex justify-center`}>
            <div className="bg-white text-brand rounded-lg border border-slate-300 py-4 w-full  mx-4 shadow-lg">
              <div className="flex flex-col gap-1 border-b px-2 pb-2">
                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <Link
                        to={item.href}
                        className={classNames(
                          active ? 'bg-slate-50 text-brand-electric rounded' : '',
                          'flex items-center gap-1.5 px-2 py-1 font-medium transition'
                        )}>
                        <item.icon className="h-4 w-4"/>
                        {item.name}
                      </Link>
                    )}
                  </Menu.Item>
                ))}
                {organizationType === OrgTypes.PART91 && !isMobileApp() && mroEmployeeProfile &&
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? 'bg-slate-50 text-brand-electric rounded' : '',
                          'flex items-center gap-1.5 px-2 py-1 w-full text-left font-medium transition'
                        )}
                        onClick={async () => {
                          setOrganizationType(OrgTypes.PART145);
                          navigate('/mro/dashboard');
                        }}
                      >
                        <ArrowsRightLeftIcon className="h-4 w-4"/>
                        Switch to MRO
                      </button>
                    )}
                  </Menu.Item>
                }
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={classNames(
                        active ? 'bg-slate-50 text-brand-electric rounded' : '',
                        'flex items-center gap-1.5 px-2 py-1 w-full text-left font-medium transition'
                      )}
                      onClick={() => {
                        logout()
                          .then(() => {
                            navigate(altLoginUrl ?? '/auth');
                          })
                          .catch(console.error);
                      }}>
                      <ArrowRightStartOnRectangleIcon className="h-4 w-4"/>
                      Sign Out
                    </button>
                  )}
                </Menu.Item>
              </div>
              {organizationType === OrgTypes.PART91 && 
                <div className="px-4 pt-2">
                  <span className="text-xs font-medium uppercase">
                    {organizations?.length === 1 ? "Organization" : "Switch Organizations"}
                  </span>
                  <select 
                    disabled={organizations?.length === 1}
                    className="rounded border border-slate-300 bg-slate-50 h-12 md:h-10 mt-1 mb-2 px-2 text-brand focus:shadow-inner placeholder:text-slate-400 placeholder:text-sm w-full pr-8 text-ellipsis"
                    onChange={async (e) => {
                      setOrganizationId(e.target.value);
                      setCraftId(null);
                      const result = await switchOrganization({ variables: { organizationId: e.target.value } });
                      if (result.data?.switchOrganization) {
                        await setLoginSession(result.data.switchOrganization.accessToken, result.data.switchOrganization.refreshToken);
                      }
                      await client.resetStore();
                      navigate('/crafts');
                    }}
                  >
                    {organizations?.map((item) => (
                      <option selected={organizationId === item.id} value={item.id} key={item.id}>{item.name}</option>
                    ))}
                  </select>
                  <Menu.Item key={'newOrg'}>
                    <Button 
                      icon={<PlusIcon className="h-4 w-4 mr-1"/>} 
                      text="New Organization" size="xs" className="w-full" 
                      color="navy"
                      onClick={() => {
                        navigate('/entities/new');
                      }}
                    />
                  </Menu.Item>
                </div>
              }
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    );
}

